/** Get suggestions from all plugins */

import { ChordLib, NoteLib, isChordNotes } from '@tunasong/music-lib'
import type { Chord, MusicContextType } from '@tunasong/music-lib'
import type { Suggestion, SuggestionContext } from '@tunasong/models'
import type { TunaEditor, TunaPlugin } from '@tunasong/plugin-lib'

interface GetSuggestions {
  editor: TunaEditor
  plugins: TunaPlugin[]
  context: MusicContextType | null
  showContexts: SuggestionContext[]
}

/**
 * GH #1298: identity of a chord suggestion for dedup = its chromatic
 * pitch-class set, scoped by `context` (per Bowie's music-correctness ruling).
 *
 * The two chord engines spell roots via different paths — the rule-based
 * `theory:nextChord` engine spells scale-derived, the `lstm:cowriter` engine
 * spells from `ChordLib.fromName(modelString)` — so the same-sounding chord can
 * arrive as `Bb` vs `A#` or `C` vs `CM`. Deduping on the display `name` or on
 * `ChordLib.equals` (a spelled-note comparison) under-dedupes those. Reduce the
 * structured `value` to notes, map to chromatic indices (0-11), dedupe + sort.
 * Prefix with `context` so a 'next' and a 'substitute' proposal for the same
 * chord never collapse into one. Returns null for non-chord suggestions, which
 * are left untouched.
 */
const chordDedupKey = (s: Suggestion): string | null => {
  if (s.type !== 'chord') {
    return null
  }
  const value = s.value as Chord
  const notes = isChordNotes(value) ? value.notes : ChordLib.getNotes(value)
  const pitchClasses = [...new Set(notes.map(n => NoteLib.getNoteChromaticIndex(n)))]
    .filter(i => i >= 0)
    .sort((a, b) => a - b)
  if (pitchClasses.length === 0) {
    return null
  }
  return `${s.context}:${pitchClasses.join(',')}`
}

// Equal-score collision tie-break: prefer the rule-based engine. Musically the
// two are identical (same chord, same context); this only makes the kept
// suggestion — and therefore the explainer's source attribution — deterministic
// instead of flickering with plugin order (Bowie's note; UX, not music).
const sourceRank = (s: Suggestion): number => (s.source === 'theory:nextChord' ? 0 : 1)

/**
 * Sort by score (descending), then drop duplicate chord suggestions keeping the
 * highest-scored of each chromatic-pitch-class set. Pure + exported so the dedup
 * is unit-testable without mocking plugins/editor (GH #1298).
 */
export const dedupeChordSuggestions = (suggestions: Suggestion[]): Suggestion[] => {
  const sorted = [...suggestions].sort((a, b) =>
    a.score === b.score ? sourceRank(a) - sourceRank(b) : a.score > b.score ? -1 : 1
  )
  const seen = new Set<string>()
  return sorted.filter(s => {
    const key = chordDedupKey(s)
    if (key === null) {
      return true
    }
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

export const getSuggestions = async ({ plugins, context, editor, showContexts }: GetSuggestions) => {
  if (!context) {
    return []
  }
  const p = plugins.map(async (p: TunaPlugin) => (p.getSuggestions ? p.getSuggestions(editor, context as never) : []))
  const suggestions = await Promise.all(p)
  const filt = suggestions.flat().filter(s => showContexts.includes(s.context))

  /** Sort by score and collapse duplicate chord suggestions (GH #1298). */
  return dedupeChordSuggestions(filt)
}
